import { Link } from 'wouter';
import { useNextStep } from '../lib/nextStep';

interface Props {
  projectId: string;
}

/**
 * The "what next" rail. Mounted at the top of every phase page (Strategy,
 * Content, Posts, Draft inspector). Same component everywhere — reads project
 * state through useNextStep and renders the one-action recommendation.
 *
 * Renders nothing while loading or when the state machine has no useful
 * recommendation to make.
 */
export function NextStep({ projectId }: Props) {
  const step = useNextStep(projectId);
  if (step.state === 'loading' || !step.headline) return null;

  const toneClasses =
    step.tone === 'amber'
      ? { border: 'border-accent-amber/40', bg: 'bg-accent-amber/5', accent: 'text-accent-amber' }
      : step.tone === 'cyan'
        ? { border: 'border-accent-cyan/40', bg: 'bg-accent-cyan/5', accent: 'text-accent-cyan' }
        : { border: 'border-accent-emerald/40', bg: 'bg-accent-emerald/5', accent: 'text-accent-emerald' };

  return (
    <section
      className={`rounded-xl border ${toneClasses.border} ${toneClasses.bg} px-5 py-4 flex items-center justify-between gap-4 flex-wrap`}
      aria-label="Next step"
    >
      <div className="min-w-0 flex-1">
        <p className={`font-mono text-[10px] uppercase tracking-[0.22em] ${toneClasses.accent} mb-1`}>
          Next step
        </p>
        <h3 className="font-display text-lg font-bold text-text-primary">{step.headline}</h3>
        {step.helper && <p className="text-xs text-muted mt-1 leading-relaxed">{step.helper}</p>}
      </div>
      {step.ctaLabel && step.ctaHref && (
        <Link
          href={step.ctaHref}
          className="btn-bracket bg-primary text-white px-4 py-2.5 text-sm rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 shrink-0 font-mono uppercase tracking-wider"
        >
          {step.ctaLabel}
          <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
        </Link>
      )}
    </section>
  );
}
